import { useState, useEffect, useCallback } from "react";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// Data
import { vanGallery } from "pages/CarRentals/sections/data/carRentalData";

function VanGallery() {
  const [activeIndex, setActiveIndex] = useState(null);
  const open = activeIndex !== null;

  const handleOpen = (index) => setActiveIndex(index);
  const handleClose = () => setActiveIndex(null);
  const handleNav = useCallback((direction) => {
    setActiveIndex((prev) => (prev + direction + vanGallery.length) % vanGallery.length);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (e) => {
      if (e.key === "ArrowLeft") handleNav(-1);
      else if (e.key === "ArrowRight") handleNav(1);
      else if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, handleNav]);

  const activePhoto = open ? vanGallery[activeIndex] : null;

  return (
    <MKBox component="section" py={6}>
      <Container>
        <MKTypography variant="h3" textAlign="center" mb={1}>
          A closer look at the van
        </MKTypography>
        <MKTypography variant="body2" color="text" textAlign="center" mb={4}>
          Click any photo to view it full size and flip through the gallery.
        </MKTypography>
        {/* Uniform 4:3 tiles. The three landscape photos are already 4:3 so they show in full;
            the portrait rear shot is center-cropped (cover + center), trimming top and bottom
            equally. The lightbox below still shows every photo full/uncropped. */}
        <Grid container spacing={2}>
          {vanGallery.map((photo, index) => (
            <Grid item xs={12} sm={6} key={photo.src}>
              <MKBox
                onClick={() => handleOpen(index)}
                sx={{
                  cursor: "pointer",
                  overflow: "hidden",
                  aspectRatio: "4 / 3",
                  borderRadius: ({ borders: { borderRadius } }) => borderRadius.lg,
                  boxShadow: ({ boxShadows: { lg } }) => lg,
                  "& img": {
                    display: "block",
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center",
                    transition: "transform 0.35s ease",
                  },
                  "&:hover img": { transform: "scale(1.05)" },
                }}
              >
                <MKBox component="img" src={photo.src} alt={photo.alt} loading="lazy" />
              </MKBox>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Lightbox */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        PaperProps={{ sx: { bgcolor: "rgba(0,0,0,0.95)" } }}
      >
        {activePhoto && (
          <MKBox display="flex" flexDirection="column" height="100%">
            <IconButton
              onClick={handleClose}
              sx={{ position: "absolute", top: 8, right: 8, color: "#fff !important", zIndex: 2 }}
            >
              <Icon>close</Icon>
            </IconButton>
            <MKBox
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="relative"
              overflow="hidden"
              px={{ xs: 5, md: 8 }}
              py={2}
            >
              <IconButton
                onClick={() => handleNav(-1)}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: { xs: 0, md: 8 },
                  transform: "translateY(-50%)",
                  color: "#fff !important",
                  zIndex: 2,
                }}
              >
                <Icon sx={{ fontSize: "48px !important" }}>chevron_left</Icon>
              </IconButton>
              <IconButton
                onClick={() => handleNav(1)}
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: { xs: 0, md: 8 },
                  transform: "translateY(-50%)",
                  color: "#fff !important",
                  zIndex: 2,
                }}
              >
                <Icon sx={{ fontSize: "48px !important" }}>chevron_right</Icon>
              </IconButton>
              <MKBox
                component="img"
                src={activePhoto.src}
                alt={activePhoto.alt}
                sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
              />
            </MKBox>
            <MKBox px={3} py={1.5} textAlign="center">
              <MKTypography variant="body2" color="white" fontWeight="bold">
                {activePhoto.alt}
              </MKTypography>
              <MKTypography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.6)" }}
                display="block"
                mt={0.5}
              >
                {activeIndex + 1} / {vanGallery.length}
              </MKTypography>
            </MKBox>
          </MKBox>
        )}
      </Dialog>
    </MKBox>
  );
}

export default VanGallery;
